"use client";

import type { DefaultError, UseMutationOptions } from "@tanstack/react-query";
import { useMutation } from "@tanstack/react-query";

import { useOpenApiClient } from "../../../providers/OpenApiProvider";
import type { paths } from "../../generated/schema";
import { assertOpenApiOk } from "./assertOpenApiOk";

type Paths = keyof paths;
export type Methods = "DELETE" | "PATCH" | "POST" | "PUT";
export type MethodPaths<Method extends Methods> = {
  [Path in Paths]: paths[Path] extends {
    [method in Lowercase<Method>]: unknown;
  }
    ? Path
    : never;
}[Paths];
export type MethodBody<
  Path extends Paths,
  Method extends Methods,
> = paths[Path] extends {
  [method in Lowercase<Method>]: {
    requestBody: { content: { "application/json": infer Body } };
  };
}
  ? Body
  : undefined;
export type MethodParameters<
  Path extends Paths,
  Method extends Methods,
> = paths[Path] extends {
  [method in Lowercase<Method>]: { parameters: infer Parameters };
}
  ? Parameters
  : undefined;
export type MethodResponse<
  Path extends Paths,
  Method extends Methods,
> = paths[Path] extends {
  [method in Lowercase<Method>]: {
    responses: { 200: { content: { "application/json": infer Response } } };
  };
}
  ? Response
  : void;
export type MethodVariables<Path extends Paths, Method extends Methods> = {
  body?: MethodBody<Path, Method>;
  params?: MethodParameters<Path, Method>;
};

export type UseOpenApiMutationOptions<
  Path extends Paths,
  Method extends Methods,
  TData = MethodResponse<Path, Method>,
  TVariables = MethodVariables<Path, Method>,
> = Omit<UseMutationOptions<TData, DefaultError, TVariables>, "mutationFn"> & {
  urlBackendPath: Path;
  method: Method;
};

export const useOpenApiMutation = <
  Method extends Methods,
  Path extends MethodPaths<Method>,
  TData = MethodResponse<Path, Method>,
>({
  method,
  urlBackendPath,
  ...options
}: UseOpenApiMutationOptions<Path, Method, TData>) => {
  const { openApiClient } = useOpenApiClient();

  return useMutation({
    mutationFn: async (variables: MethodVariables<Path, Method>) => {
      const { data, error, response } = await (openApiClient[method] as any)(
        urlBackendPath,
        { ...variables },
      );

      assertOpenApiOk(error, response);

      return data as TData;
    },
    ...options,
  });
};
